import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { LoadingButton } from "@mui/lab";
import BasicDialog from "../../components/base/BasicDialog";
import { FilterTableWrapper } from "../../components/table/BetterFilterTable/BetterFilterTable";
import LoaderWrapper from "../../components/wrapper/LoaderWrapper";
import usePaginationWithLocalSearchParamsAndOrdering from "../../hooks/usePaginationWithLocalSearchParamsAndOrdering";
import {
    useInvestments,
} from "../../services/productsService";
import { AgreementPassportButton } from "../AgreementsPage/AgreementPassportDialog/AgreementPassportButton";
import { useShortDataAgreements } from "../../services/agreementsService";
import { TEXT_FIELD_TYPE, SELECT_FIELD_TYPE } from "../../helpers/constants";
import {Checkbox} from "@mui/material";
import toast from "react-hot-toast";
import { SnackbarSuccess } from "../../components/base/SnackbarAlert/SnackbarSuccess";
import { useToasts } from "../../hooks/useToasts";
import { useUpdateBankingMutation } from "../../services/bankingService";
import { useForm } from "react-hook-form";

export function MatchBankingWithAgreementDialog(props) {
    const { t } = useTranslation();

    const [agreementsDataLocal, setAgreementsDataLocal] = useState([]);
    const [agreementsCountLocal, setAgreementsCountLocal] = useState(0);

    const pagination = usePaginationWithLocalSearchParamsAndOrdering();

    const agreeemntSearchParams = useMemo(() => {
        const params = new URLSearchParams(pagination.searchParams);
        params.set("page", String(pagination.page));
        params.set("page_size", String(pagination.pageSize));
        params.set("investment", props.investmentId)
        params.set("exclude_merged_agreements_with_banking_withdrawal", props.bankingIsWithdrawal)
        params.set("exclude_reservation_agreements", true)
        return params;
    }, [pagination.searchParams, pagination.page, pagination.pageSize]);

    const { data: agreementsData, isLoading } = useShortDataAgreements(agreeemntSearchParams);

    useEffect(() => {
        if (agreementsData?.results) {
            setAgreementsDataLocal(agreementsData?.results);
            setAgreementsCountLocal(agreementsData.count);
        }
    }, [agreementsData]);

    const { data: investments, isLoading: isInvestmentLoading } = useInvestments({
        select: (data) =>
            data.results.map((investment) => ({
                value: investment.id,
                label: investment.investment_name_sales,
            })),
    });

    const [selectedAgreementId, setSelectedAgreementId] = useState()
    const handleSelectAgreementId = (id) => {
        setSelectedAgreementId(id)
    };

    const handleUnselectAgreementId = (id) => {
        setSelectedAgreementId(null)
    };

    const { onFormErrors, onServerError } = useToasts();
    const { mutate: updateBanking, isPending: isUpdatingBanking } = useUpdateBankingMutation({
            onSuccess: () => {
                toast.custom(
                    () => <SnackbarSuccess message={t("successfullyMerged")} />,
                    {
                        position: "bottom-right",
                    },
                );
                props.onClose()
            },
            onError: (error) => {
                onServerError(error);
            },
        });

    const { handleSubmit } = useForm({defaultValues: {}});

    const onSubmit = (data) => {
        updateBanking({data:{banking_agreement: selectedAgreementId}, id:props.bankingId})
    }; 

    return (
        <BasicDialog
            open={true}
            onClose={() => props.onClose()}
            titleAlign={"center"}
            contentAlign={"center"}
            title={t("bankings.mergeBankingWithAgreement")}
            maxWidth={"lg"}
            showDialogActions
            alwaysFullScreen={false}
            contentSx={{ pt: 0 }}
        >
            <LoaderWrapper showLoader={isInvestmentLoading || !agreementsDataLocal}>
                <FilterTableWrapper
                    data={agreementsDataLocal}
                    countRecords={agreementsCountLocal}
                    localPagination={pagination}
                    filtersOpts={{
                        showCleanFilterIcon: true,
                    }}
                    tableStyle={{
                        maxHeight: "80vh",
                        overflow: "auto",
                    }}
                    onSearchFiltersChange={pagination.handleChangeSearchParams}
                    fieldsConfig={[
                        {
                            name: "checkboox",
                            filterType: null,
                            label: t("select"),
                            getValueCallback: (row) => (
                                <Checkbox
                                    checked={selectedAgreementId===row.id}
                                    onChange={(e) => {
                                        if (e.currentTarget.checked) {
                                            handleSelectAgreementId(row.id);
                                        } else {
                                            handleUnselectAgreementId(row.id);
                                        }
                                    }}
                                />
                            ),
                        },
                        {
                            name: "agreement_nr",
                            getValueCallback: (rowData) => rowData.agreement_nr,
                            label: t("table.agreements.agreementNr"),
                            filterType: TEXT_FIELD_TYPE,
                            getFilterFieldConfigCallback: () => undefined,
                            sortable: true,
                        },
                        {
                            name: "customer_full_name",
                            getValueCallback: (rowData) => rowData.agreement_customer_person, 
                            label: t("table.agreements.customerFullNames"),
                            filterType: TEXT_FIELD_TYPE,
                            getFilterFieldConfigCallback: () => undefined,
                            sortable: false,
                        },
                        {
                            name: "counterparty_names",
                            getValueCallback: (rowData) => rowData.agreement_customer_company,
                            label: t("table.agreements.companyNames"),
                            filterType: TEXT_FIELD_TYPE,
                            getFilterFieldConfigCallback: () => undefined,
                            sortable: false,
                        },
                        {
                            name: "investment",
                            getValueCallback: (rowData) => rowData.agreement_invest.investment_name_sales,
                            label: t("table.agreements.investment"),
                            filterType: SELECT_FIELD_TYPE,
                            getFilterFieldConfigCallback: () => ({
                                optionKey: "value",
                                optionLabel: "label",
                                options: investments,
                                multiple: false,
                            }),
                            fieldConfig: {
                                optionKey: "value",
                                optionLabel: "label",
                                options: investments,
                            },
                            sortable: false,
                        },
                        {
                            name: "agreement_notarial_deed",
                            getValueCallback: (rowData) => rowData.agreement_notarial_deed || "",
                            label: t("table.agreements.agreementNotarialDeed"),
                            filterType: TEXT_FIELD_TYPE,
                            getFilterFieldConfigCallback: () => undefined,
                            sortable: true,
                        },
                        {
                            name: "product_numbers",
                            getValueCallback: (rowData) => rowData.agreement_local,
                            label: t("table.agreements.productNumbers"),
                            filterType: TEXT_FIELD_TYPE,
                            getFilterFieldConfigCallback: () => undefined,
                            sortable: false,
                        },
                        {
                            name: "passport",
                            getValueCallback: (rowData) => <AgreementPassportButton agreement={{id:rowData.id}} />,
                            label: t("table.agreements.passport"),
                            getFilterFieldConfigCallback: () => undefined,
                            sortable: false,
                        },
                    ]}
                />
            </LoaderWrapper>
            <LoadingButton
                loading={isUpdatingBanking}
                disabled={!selectedAgreementId}
                variant="contained"
                color="primary"
                sx={{ mt: 2 }}
                onClick={handleSubmit(onSubmit, onFormErrors)}
                fullWidth
            >
                {t("merge")}
            </LoadingButton>
        </BasicDialog>
    );
}
